import { Bug, Calculator, Lightbulb } from 'lucide-react';
import type { ComponentType } from 'react';
import type { BadgeTone } from '@/components/ds';
import type { TicketStatus, TicketType } from '@/api/feedback.api';

/**
 * How internal vocabulary is presented to a clinic.
 *
 * The statuses stored in the database are for the team's queue. A dentist
 * reading "in_progress" learns nothing they did not already assume, and
 * "wont_do" reads as a slammed door. Each status therefore has a plain-language
 * label *and* a sentence saying what actually happens next — the second is the
 * part that stops a status chip from being decoration.
 *
 * Everything here is an i18n key, not a string: this file decides the mapping,
 * the locale files decide the words, in all four languages.
 */

export const TYPE_META: Record<
  TicketType,
  {
    labelKey: string;
    descriptionKey: string;
    icon: ComponentType<{ className?: string }>;
  }
> = {
  bug: {
    labelKey: 'feedback.type.bug.label',
    descriptionKey: 'feedback.type.bug.description',
    icon: Bug,
  },
  incorrect_data: {
    labelKey: 'feedback.type.incorrectData.label',
    descriptionKey: 'feedback.type.incorrectData.description',
    icon: Calculator,
  },
  feature: {
    labelKey: 'feedback.type.feature.label',
    descriptionKey: 'feedback.type.feature.description',
    icon: Lightbulb,
  },
};

export interface StatusMeta {
  /** Short label for a chip. */
  labelKey: string;
  /** One sentence on what this means and what happens next. */
  meaningKey: string;
  tone: BadgeTone;
}

export const STATUS_META: Record<TicketStatus, StatusMeta> = {
  new: {
    labelKey: 'feedback.status.new.label',
    meaningKey: 'feedback.status.new.meaning',
    tone: 'neutral',
  },
  acknowledged: {
    labelKey: 'feedback.status.acknowledged.label',
    meaningKey: 'feedback.status.acknowledged.meaning',
    tone: 'info',
  },
  planned: {
    labelKey: 'feedback.status.planned.label',
    meaningKey: 'feedback.status.planned.meaning',
    tone: 'info',
  },
  in_progress: {
    labelKey: 'feedback.status.inProgress.label',
    meaningKey: 'feedback.status.inProgress.meaning',
    tone: 'primary',
  },
  resolved: {
    labelKey: 'feedback.status.resolved.label',
    meaningKey: 'feedback.status.resolved.meaning',
    tone: 'success',
  },
  wont_do: {
    labelKey: 'feedback.status.wontDo.label',
    meaningKey: 'feedback.status.wontDo.meaning',
    tone: 'warning',
  },
};

/**
 * Fallback for a status the client does not know.
 *
 * The server's vocabulary can gain a value before a tab is reloaded. Showing
 * the raw token is ugly but honest; crashing the list is not an option, and
 * silently mapping it to "new" would be a lie.
 */
export const UNKNOWN_STATUS: StatusMeta = {
  labelKey: 'feedback.status.unknown.label',
  meaningKey: 'feedback.status.unknown.meaning',
  tone: 'neutral',
};

export const statusMeta = (status: string): StatusMeta =>
  STATUS_META[status as TicketStatus] ?? UNKNOWN_STATUS;
